import {
  systemCouponControllerSearchCoupons,
  systemCouponControllerGetCouponById,
  systemCouponControllerCreateCoupon,
  systemCouponControllerUpdateCoupon,
  systemCouponControllerDeleteCoupon,
  systemCouponControllerActivateCoupon,
  systemCouponControllerDeactivateCoupon,
} from "../../api/generated";

// SYSTEM_ADMIN coupons — backed by SystemCouponController (/system/coupon/*). Migrated to
// the OpenAPI-generated client where the spec models the endpoint; auth + timezone come from
// configureApi's interceptor (per-call token reads removed). Coupon endpoints return their
// DTOs UN-enveloped, so `data` == the old axios `response.data` and the cached objects keep
// the same {data} shape callers expect.

let couponCache = {};
let couponByIdCache = {};

// Migrated to the OpenAPI client (GET /system/coupon/search). The server now models paging as
// FLAT page/size/sort query params (springdoc @ParameterObject), so the SDK serializes them in a
// form Spring binds. `keyword` maps to the `code` query param. Returns {data} = the page body,
// preserving the raw-axios contract callers read (res.data.content/numberOfElements).
export const getCoupons = async (pageId = 0, pageSize = 20, keyword, companyId, isActive) => {
    const {data, error} = await systemCouponControllerSearchCoupons({
        query: {
            page: pageId,
            size: pageSize,
            ...(keyword ? {code: keyword} : {}),
            ...(companyId !== null && companyId !== undefined ? {companyId} : {}),
            ...(isActive !== null && isActive !== undefined ? {isActive} : {}),
        },
    });
    if (error) {
        throw error;
    }
    return {data};
};

export const getCouponById = async (id) => {
    if (!couponByIdCache[id]) {
        const {data, error} = await systemCouponControllerGetCouponById({path: {id}});
        if (error) throw error;

        couponByIdCache[id] = {data};
    }

    return couponByIdCache[id];
};

export const saveCoupon = async (couponData) => {
    // Clear cache when saving
    couponCache = {};
    couponByIdCache = {};

    const requestData = {
        companyId: couponData.companyId,
        code: couponData.code.toUpperCase(),
        discountPercentage: couponData.discountPercentage,
        validUntil: couponData.validUntil,
        maxUsageCount: couponData.maxUsageCount
    };

    // Include duration if provided
    if (couponData.duration) {
        requestData.duration = couponData.duration;
    }

    const {data, error} = await systemCouponControllerCreateCoupon({body: requestData});
    if (error) throw error;
    return {data};
};

export const updateCoupon = async (id, couponData) => {
    // Clear cache when updating
    couponCache = {};
    couponByIdCache = {};

    const requestData = {};

    if (couponData.companyId !== undefined) {
        requestData.companyId = couponData.companyId;
    }
    if (couponData.code !== undefined) {
        requestData.code = couponData.code.toUpperCase();
    }
    if (couponData.discountPercentage !== undefined) {
        requestData.discountPercentage = couponData.discountPercentage;
    }
    if (couponData.validUntil !== undefined) {
        requestData.validUntil = couponData.validUntil;
    }
    if (couponData.maxUsageCount !== undefined) {
        requestData.maxUsageCount = couponData.maxUsageCount;
    }
    if (couponData.isActive !== undefined) {
        requestData.isActive = couponData.isActive;
    }
    if (couponData.duration !== undefined) {
        requestData.duration = couponData.duration;
    }

    const {data, error} = await systemCouponControllerUpdateCoupon({path: {id}, body: requestData});
    if (error) throw error;
    return {data};
};

export const deleteCoupon = async (id) => {
    // Clear cache when deleting
    couponCache = {};
    couponByIdCache = {};

    const {data, error} = await systemCouponControllerDeleteCoupon({path: {id}});
    if (error) throw error;
    return {data};
};

export const toggleCouponStatus = async (id, isActive) => {
    // Clear cache when toggling status
    couponCache = {};
    couponByIdCache = {};

    const {data, error} = isActive
        ? await systemCouponControllerActivateCoupon({path: {id}})
        : await systemCouponControllerDeactivateCoupon({path: {id}});
    if (error) throw error;
    return {data};
};

// Clear all caches (useful for global refresh)
export const clearCouponCache = () => {
    couponCache = {};
    couponByIdCache = {};
};
